export interface Badge {
  id: string;
  name: string;
  condition: string;
  visualStyle: string;
  shareableQuote: string;
  icon: string; // Lucide icon name or emoji
  color: string; // Tailwind color class
  tier: 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond' | 'legendary';
}

export interface UserBadge {
  id: string;
  user_id: string;
  badge_id: string;
  unlocked_at: string;
  notified: boolean;
}

export const BADGES: Badge[] = [
  {
    id: 'first-blood',
    name: 'First Blood',
    condition: 'Complete first workout log',
    visualStyle: 'Red dripping sword',
    shareableQuote: 'First Blood — Evolver [Name] just started the war on mediocrity',
    icon: '🗡️',
    color: 'text-red-500',
    tier: 'bronze',
  },
  {
    id: '7-day-streak',
    name: '7-Day Streak',
    condition: '7 consecutive log days',
    visualStyle: 'Small flame',
    shareableQuote: '7-Day Streak — Evolver [Name] is on fire 🔥',
    icon: '🔥',
    color: 'text-orange-500',
    tier: 'bronze',
  },
  {
    id: 'pr-assassin',
    name: 'PR Assassin',
    condition: 'Beat any personal record',
    visualStyle: 'Skull with barbell',
    shareableQuote: 'PR Assassin — Evolver [Name] murdered their old bench PR',
    icon: '💀',
    color: 'text-zinc-400',
    tier: 'silver',
  },
  {
    id: '30-day-immortal',
    name: '30-Day Immortal',
    condition: '30-day streak',
    visualStyle: 'Bronze crown',
    shareableQuote: '30-Day Immortal — Evolver [Name] is no longer human',
    icon: '👑',
    color: 'text-amber-600',
    tier: 'bronze',
  },
  {
    id: 'nutrition-ninja',
    name: 'Nutrition Ninja',
    condition: 'Log food 10 days straight',
    visualStyle: 'Green shuriken',
    shareableQuote: 'Nutrition Ninja — Evolver [Name] locked in their diet for 10 days',
    icon: '🥷',
    color: 'text-green-500',
    tier: 'silver',
  },
  {
    id: '100lb-club',
    name: '100 lb Club',
    condition: 'Add 100+ lbs total to big-3 since day 1',
    visualStyle: 'Gold "100" badge',
    shareableQuote: '100 lb Club — Evolver [Name] evolved their total +100 lbs',
    icon: '💯',
    color: 'text-yellow-500',
    tier: 'gold',
  },
  {
    id: '250lb-club',
    name: '250 lb Club',
    condition: 'Add 250+ lbs total to big-3',
    visualStyle: 'Silver "250" badge',
    shareableQuote: '250 lb Club — Evolver [Name] is a different species',
    icon: '🏋️',
    color: 'text-slate-400',
    tier: 'silver',
  },
  {
    id: '500lb-club',
    name: '500 lb Club',
    condition: 'Add 500+ lbs total to big-3',
    visualStyle: 'Platinum "500" badge',
    shareableQuote: '500 lb Club — Evolver [Name] is officially elite',
    icon: '⚡',
    color: 'text-cyan-400',
    tier: 'platinum',
  },
  {
    id: '750lb-club',
    name: '750 lb Club',
    condition: 'Add 750+ lbs total to big-3',
    visualStyle: 'Diamond "750" badge',
    shareableQuote: '750 lb Club — Evolver [Name] is a walking legend',
    icon: '💎',
    color: 'text-blue-400',
    tier: 'diamond',
  },
  {
    id: '1000lb-club',
    name: '1000 lb Club',
    condition: 'Add 1000+ lbs total to big-3',
    visualStyle: 'Black diamond "1000" badge + skull',
    shareableQuote: '1000 lb Club — Evolver [Name] has ascended beyond human limits ☠️',
    icon: '☠️',
    color: 'text-purple-500',
    tier: 'legendary',
  },
  {
    id: 'century-club',
    name: 'Century Club',
    condition: '100 workout days logged',
    visualStyle: 'Gold "100" circle',
    shareableQuote: 'Century Club — Evolver [Name] has 100 days of proof',
    icon: '🎯',
    color: 'text-yellow-500',
    tier: 'gold',
  },
  {
    id: 'double-century',
    name: 'Double Century',
    condition: '200 workout days logged',
    visualStyle: 'Double gold circle',
    shareableQuote: 'Double Century — Evolver [Name] is immortal',
    icon: '🌟',
    color: 'text-yellow-400',
    tier: 'gold',
  },
  {
    id: 'form-fixer',
    name: 'Form Fixer',
    condition: 'Upload & complete 5 form-check fixes',
    visualStyle: 'Red checkmark',
    shareableQuote: 'Form Fixer — Evolver [Name] perfected their squat',
    icon: '✅',
    color: 'text-red-500',
    tier: 'silver',
  },
  {
    id: 'plate-breaker',
    name: 'Plate Breaker',
    condition: 'Deadlift or squat 4+ plates (405+ lbs)',
    visualStyle: 'Cracked plate',
    shareableQuote: 'Plate Breaker — Evolver [Name] just destroyed 4 plates',
    icon: '💥',
    color: 'text-orange-600',
    tier: 'platinum',
  },
  {
    id: '10k-steps-soldier',
    name: '10k Steps Soldier',
    condition: 'Complete a 7-day 10k steps challenge',
    visualStyle: 'Boot print',
    shareableQuote: '10k Steps Soldier — Evolver [Name] marched through the challenge',
    icon: '🥾',
    color: 'text-emerald-600',
    tier: 'bronze',
  },
  {
    id: 'macro-master',
    name: 'Macro Master',
    condition: 'Hit macros within 5% for 14 days straight',
    visualStyle: 'Gold scale',
    shareableQuote: 'Macro Master — Evolver [Name] eats like a scientist',
    icon: '⚖️',
    color: 'text-yellow-500',
    tier: 'gold',
  },
  {
    id: 'no-excuses',
    name: 'No Excuses',
    condition: 'Zero missed sessions in a 30-day program',
    visualStyle: 'Iron shield',
    shareableQuote: 'No Excuses — Evolver [Name] showed up every single day',
    icon: '🛡️',
    color: 'text-slate-500',
    tier: 'silver',
  },
  {
    id: 'early-bird',
    name: 'Early Bird',
    condition: 'Log 30 workouts before 7 AM',
    visualStyle: 'Sunrise rooster',
    shareableQuote: 'Early Bird — Evolver [Name] owns the morning',
    icon: '🐓',
    color: 'text-orange-400',
    tier: 'gold',
  },
  {
    id: 'night-owl',
    name: 'Night Owl',
    condition: 'Log 30 workouts after 9 PM',
    visualStyle: 'Moon owl',
    shareableQuote: 'Night Owl — Evolver [Name] trains when the world sleeps',
    icon: '🦉',
    color: 'text-indigo-500',
    tier: 'gold',
  },
  {
    id: 'evolver-founder',
    name: 'Evolver Founder',
    condition: 'Refer 5 friends who hit Day 30',
    visualStyle: 'Crown + referral icon',
    shareableQuote: 'Evolver Founder — Evolver [Name] built the cult',
    icon: '👥',
    color: 'text-purple-500',
    tier: 'platinum',
  },
  {
    id: 'legend',
    name: 'Legend',
    condition: 'Reach Day 90',
    visualStyle: 'Purple legend shield',
    shareableQuote: 'EVOLVER LEGEND — Top 0.8%',
    icon: '🏆',
    color: 'text-purple-500',
    tier: 'platinum',
  },
  {
    id: 'titan',
    name: 'Titan',
    condition: 'Reach Day 180',
    visualStyle: 'Blue titan helmet',
    shareableQuote: 'EVOLVER TITAN — Unbreakable',
    icon: '⚔️',
    color: 'text-blue-500',
    tier: 'diamond',
  },
  {
    id: 'immortal',
    name: 'Immortal',
    condition: 'Reach Day 365',
    visualStyle: 'Gold immortal crown',
    shareableQuote: 'ONE YEAR IMMORTAL — Evolver [Name] is eternal',
    icon: '👑',
    color: 'text-yellow-400',
    tier: 'legendary',
  },
  {
    id: 'ascended',
    name: 'Ascended I ☠️',
    condition: 'Every 180 days after year 1 (Day 545, 725, etc.)',
    visualStyle: 'Skull per level',
    shareableQuote: 'ASCENDED II ☠️☠️ — Evolver [Name] is no longer human',
    icon: '☠️',
    color: 'text-red-600',
    tier: 'legendary',
  },
  {
    id: 'ascended-god',
    name: 'Ascended God Tier',
    condition: 'Day 1000+',
    visualStyle: 'Black crown with 5 skulls',
    shareableQuote: 'ASCENDED GOD TIER — Evolver [Name] has transcended reality',
    icon: '🖤',
    color: 'text-zinc-900',
    tier: 'legendary',
  },
];

export const getBadgeById = (id: string): Badge | undefined => {
  return BADGES.find(b => b.id === id);
};

export const getTierColor = (tier: Badge['tier']): string => {
  switch (tier) {
    case 'bronze': return 'from-amber-600 via-orange-700 to-amber-800';
    case 'silver': return 'from-slate-400 via-slate-300 to-slate-500';
    case 'gold': return 'from-yellow-400 via-amber-500 to-yellow-500';
    case 'platinum': return 'from-cyan-400 via-teal-400 to-cyan-500';
    case 'diamond': return 'from-blue-400 via-indigo-400 to-purple-500';
    case 'legendary': return 'from-purple-600 via-fuchsia-500 to-pink-500';
  }
};

// Solid background colors for badge cards (used in grid view)
export const getTierBackground = (tier: Badge['tier']): string => {
  switch (tier) {
    case 'bronze': return 'bg-gradient-to-br from-amber-700 via-orange-600 to-amber-800';
    case 'silver': return 'bg-gradient-to-br from-slate-400 via-slate-300 to-slate-500';
    case 'gold': return 'bg-gradient-to-br from-yellow-500 via-amber-400 to-yellow-400';
    case 'platinum': return 'bg-gradient-to-br from-cyan-500 via-teal-400 to-cyan-400';
    case 'diamond': return 'bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600';
    case 'legendary': return 'bg-gradient-to-br from-purple-700 via-fuchsia-600 to-pink-600';
  }
};

export const getTierGlow = (tier: Badge['tier']): string => {
  switch (tier) {
    case 'bronze': return 'shadow-amber-700/50';
    case 'silver': return 'shadow-slate-400/50';
    case 'gold': return 'shadow-yellow-500/50';
    case 'platinum': return 'shadow-cyan-400/50';
    case 'diamond': return 'shadow-blue-400/50';
    case 'legendary': return 'shadow-purple-500/50';
  }
};
